import { MediaItem } from '../../types';
import { generateMediaItemId } from '../../utils/mediaItemUtils';

export interface SplitResult {
  firstPart: MediaItem;
  secondPart: MediaItem;
}

/**
 * Split a media item at a specific time
 * @param item - The media item to split
 * @param splitTime - The absolute time to split at (not relative to item start)
 * @returns Two new media items representing the split parts, or null if invalid
 */
export const splitMediaItem = (
  item: MediaItem,
  splitTime: number
): SplitResult | null => {
  console.log('🔪 === SPLIT DEBUG START ===');
  console.log('📦 Original Item:', {
    name: item.name,
    start: item.start,
    end: item.end,
    length: item.length,
    duration: item.end - item.start
  });
  console.log('✂️ Split Time (absolute):', splitTime);
  
  // Validate split time is within item bounds
  if (splitTime <= item.start || splitTime >= item.end) {
    console.warn('❌ Split time must be within item bounds', {
      splitTime,
      itemStart: item.start,
      itemEnd: item.end
    });
    return null;
  }

  // Calculate relative split position within the item
  const relativeTime = splitTime - item.start;
  console.log('📏 Relative Time (from item start):', relativeTime);
  
  // Generate unique IDs for the new parts
  const firstPartId = generateMediaItemId('split-part1', item.name);
  const secondPartId = generateMediaItemId('split-part2', item.name);
  
  // Create first part (from start to split point)
  const firstPart: MediaItem = {
    ...item,
    id: firstPartId,
    name: `${item.name} (Part 1)`,
    end: splitTime,
    length: relativeTime,
    // Video trimming: Part 1 plays from the original start position in the video file
    videoOffset: item.videoOffset || 0,
    videoDuration: relativeTime,
  };

  // Create second part (from split point to end)
  const secondPart: MediaItem = {
    ...item,
    id: secondPartId,
    name: `${item.name} (Part 2)`,
    start: splitTime,
    length: item.end - splitTime,
    // Video trimming: Part 2 starts from where Part 1 ended in the video file
    videoOffset: (item.videoOffset || 0) + relativeTime,
    videoDuration: item.end - splitTime,
  };

  console.log('📦 Part 1:', {
    name: firstPart.name,
    start: firstPart.start,
    end: firstPart.end,
    length: firstPart.length,
    duration: firstPart.end - firstPart.start,
    videoOffset: firstPart.videoOffset,
    videoDuration: firstPart.videoDuration,
    meaning: `Plays ${firstPart.videoDuration}s of video starting from ${firstPart.videoOffset}s in the file`
  });
  console.log('📦 Part 2:', {
    name: secondPart.name,
    start: secondPart.start,
    end: secondPart.end,
    length: secondPart.length,
    duration: secondPart.end - secondPart.start,
    videoOffset: secondPart.videoOffset,
    videoDuration: secondPart.videoDuration,
    meaning: `Plays ${secondPart.videoDuration}s of video starting from ${secondPart.videoOffset}s in the file`
  });
  console.log('✅ Verification:', {
    part1Duration: firstPart.end - firstPart.start,
    part2Duration: secondPart.end - secondPart.start,
    totalDuration: (firstPart.end - firstPart.start) + (secondPart.end - secondPart.start),
    originalDuration: item.end - item.start,
    matches: Math.abs(((firstPart.end - firstPart.start) + (secondPart.end - secondPart.start)) - (item.end - item.start)) < 0.001
  });
  console.log('🔪 === SPLIT DEBUG END ===');

  return { firstPart, secondPart };
};

/**
 * Handle split media item action with state updates
 */
export const handleSplitMediaItem = (
  itemId: string,
  splitTime: number | undefined,
  currentTime: number,
  mediaItems: MediaItem[],
  setMediaItems: React.Dispatch<React.SetStateAction<MediaItem[]>>,
  setSelectedMediaItemId: ((id: string | null) => void) | undefined,
  showNotification: (notification: any) => void
): boolean => {
  const item = mediaItems.find(m => m.id === itemId);
  if (!item) {
    showNotification({
      type: 'error',
      title: 'Split Failed',
      message: 'Media item not found'
    });
    return false;
  }

  // Use provided split time or current playhead position
  const splitAt = splitTime ?? currentTime;
  
  // Validate split is within item bounds
  if (splitAt <= item.start || splitAt >= item.end) {
    showNotification({
      type: 'warning',
      title: 'Invalid Split Position',
      message: 'Split position must be within the media item boundaries'
    });
    return false;
  }

  // Perform the split
  const result = splitMediaItem(item, splitAt);
  if (!result) {
    showNotification({
      type: 'error',
      title: 'Split Failed',
      message: 'Unable to split media item at this position'
    });
    return false;
  }

  const { firstPart, secondPart } = result;

  // Update media items: remove original, add two parts
  setMediaItems(prevItems => {
    const filtered = prevItems.filter(m => m.id !== itemId);
    const newItems = [...filtered, firstPart, secondPart].sort((a, b) => a.start - b.start);
    return newItems;
  });

  // Deselect after split
  if (setSelectedMediaItemId) {
    setSelectedMediaItemId(null);
  }

  // showNotification({
  //   type: 'success',
  //   title: 'Media Split',
  //   message: `"${item.name}" split into 2 parts at ${splitAt.toFixed(1)}s`
  // });

  return true;
};
